import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Box, Stack, Typography } from '@mui/material'

import FacebookMediaUploadComponent from '../../shared/components/ModuleComponents/FacebookMediaUploadComponent'
import { ActionCall } from '@professocial/professocialTypes/types/commonTypes'
import { FacebookPage, MediaFileMetadata } from '@3m5/sm_publish-frontend-api'
import { useDispatch, useSelector } from 'react-redux'
import {
  GetFacebookPageRequest,
  UpdateCoverImageRequest,
  UpdateProfileImageRequest
} from '@3m5/sm_publish-frontend-api'
import {
  getFacebookPageInitiated,
  // getFacebookPageInitiated,
  listCoverImagesInitiated, listProfileImagesInitiated,
  updateCoverImageInitiated,
  updateProfileImageInitiated, uploadCoverMediaFileInitiated, uploadProfileMediaFileInitiated,
} from '../../store/facebook/facebook.actions'
import {
  getCoverMediaFile,
  getProfileMediaFile,
  getCoverImages,
  getProfileImages
} from '../../store/facebook/facebook.selector'
import {
  UploadTemporaryMediaFileRequest,
} from '@3m5/sm_publish-frontend-api'
import { namedRequestData, namedRequestInProgress } from '../../store/common/common.selectors'
import { FacebookSettingsRootState } from '../../store/facebookSettingsRoot.reducer'
import Preloader from '@professocial/professocialFormComponents/FormComponents/Preloader'
import PreloaderButton from '@professocial/professocialFormComponents/FormComponents/PreloaderButton'

interface IProps {
  config: FacebookPage | undefined;
  isWizard?: boolean;
  wizardConfirmed?: boolean;
  onSetCompleted?: () => void;
  facebookParams?: GetFacebookPageRequest;
}

// Test
const UserFacebookProfile: React.FC<IProps> = (props) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const [profileImg, setProfileImg] = useState<MediaFileMetadata | undefined>()
  const [coverImg, setCoverImg] = useState<MediaFileMetadata | undefined>()
  const [loading, setLoading] = useState<{ profile: boolean, cover: boolean }>({ profile: false, cover: false })
  const [profileUploadImg, setProfileUploadImg] = useState<MediaFileMetadata | undefined>()
  const [coverUploadImg, setCoverUploadImg] = useState<MediaFileMetadata | undefined>()

  const dispatchUpdateProfileImage = useCallback((params: UpdateProfileImageRequest) => dispatch(updateProfileImageInitiated(params)), [dispatch])
  const dispatchUpdateCoverImage = useCallback((params: UpdateCoverImageRequest) => dispatch(updateCoverImageInitiated(params)), [dispatch])
  const dispatchUploadProfileMediaFile = useCallback((params: UploadTemporaryMediaFileRequest) => dispatch(uploadProfileMediaFileInitiated(params)), [dispatch])
  const dispatchUploadCoverMediaFile = useCallback((params: UploadTemporaryMediaFileRequest) => dispatch(uploadCoverMediaFileInitiated(params)), [dispatch])
  const dispatchProfileImages = useCallback(() => dispatch(listProfileImagesInitiated()), [dispatch])
  const dispatchCoverImages = useCallback(() => dispatch(listCoverImagesInitiated()), [dispatch])
  // const dispatchGetFacebookPage = useCallback((params: GetFacebookPageRequest) => dispatch(getFacebookPageInitiated(params)), [dispatch])

  const profileMetadata: MediaFileMetadata | undefined = useSelector(getProfileMediaFile)
  const coverMetadata: MediaFileMetadata | undefined = useSelector(getCoverMediaFile)
  const profileImages: MediaFileMetadata[] | undefined = useSelector(getProfileImages)
  const coverImages: MediaFileMetadata[] | undefined = useSelector(getCoverImages)
  const actionOnUpdateProfileImage: ActionCall | undefined = useSelector((state: FacebookSettingsRootState) => namedRequestData(state, updateProfileImageInitiated.type))
  const updatingProfileImage: boolean = useSelector((state: FacebookSettingsRootState) => namedRequestInProgress(state, updateProfileImageInitiated.type))
  const actionOnUpdateCoverImage: ActionCall | undefined = useSelector((state: FacebookSettingsRootState) => namedRequestData(state, updateCoverImageInitiated.type))
  const updatingCoverImage: boolean | undefined = useSelector((state: FacebookSettingsRootState) => namedRequestInProgress(state, updateCoverImageInitiated.type))
  const loadingPage: boolean | undefined = useSelector((state: FacebookSettingsRootState) => namedRequestInProgress(state, getFacebookPageInitiated.type))

  const saveChanges = () => {
    if (props.config?.networkPageId) {
      if (profileImg) {
        dispatchUpdateProfileImage({
          imageId: profileImg.mediaFileId,
          networkPageId: props.config.networkPageId,
        })
      }
      if (coverImg) {
        dispatchUpdateCoverImage({
          imageId: coverImg.mediaFileId,
          networkPageId: props.config.networkPageId,
        })
      }
      if (!coverImg && !profileImg) {
        props.wizardConfirmed && props.onSetCompleted && props.onSetCompleted()
      }
    }
  }

  useEffect(() => {
    props.wizardConfirmed && saveChanges()
  }, [props.wizardConfirmed])

  useEffect(() => {
    dispatchCoverImages()
    dispatchProfileImages()
  }, [],
  )

  useEffect(() => {
    if (profileMetadata) {
      setLoading({ ...loading, profile: false })
      setProfileUploadImg(profileMetadata)
    }
  }, [profileMetadata])

  useEffect(() => {
    if (coverMetadata) {
      setLoading({ ...loading, cover: false })
      setCoverUploadImg(coverMetadata)
    }
  }, [coverMetadata])

  useEffect(() => {
    if (actionOnUpdateProfileImage?.status === 'success') {
      setProfileImg(undefined)
      setProfileUploadImg(undefined)
    }
    if (actionOnUpdateCoverImage?.status === 'success') {
      setCoverImg(undefined)
      setCoverUploadImg(undefined)
    }
  }, [actionOnUpdateProfileImage, actionOnUpdateCoverImage])

  return (
    <>
      {!props.isWizard &&
        <Typography variant="h4" sx={{ mb: 3 }}>{t('facebookSettingsCommon:yourProfileImage')}</Typography>}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={{ xs: 1, sm: 2, md: 4 }}
      >
        <Box minWidth="180px">
          {loading.profile || loadingPage ? <Preloader minHeight="180px"/>
            : (
              <FacebookMediaUploadComponent
                src={`https://graph.facebook.com/${props.config?.networkPageId}/picture?width=180&height=180`}
                mediaData={profileImages}
                uploadImg={profileUploadImg}
                setUploadImg={(mediaFileMetadata: MediaFileMetadata | undefined) => setProfileUploadImg(mediaFileMetadata)}
                pictureType="profile"
                aspectRatio={1 / 1}
                cropperOptions={{ width: 180, height: 180 }}
                getMediaData={(params) => {
                  setLoading({ cover: false, profile: true })
                  dispatchUploadProfileMediaFile(params)
                }}
                updateImage={(mediaFileMetadata: MediaFileMetadata | undefined) => setProfileImg(mediaFileMetadata)}
                width="180px"
                height="180px"
                useRoundImageBorders
              />)}
        </Box>
        <Box flexGrow={1}>
          {loading.cover || loadingPage ? <Preloader minHeight="340px"/>
            : (
              <FacebookMediaUploadComponent
                src={props.config?.titleImageUrl}
                mediaData={coverImages}
                uploadImg={coverUploadImg}
                setUploadImg={(mediaFileMetadata: MediaFileMetadata | undefined) => setCoverUploadImg(mediaFileMetadata)}
                pictureType="cover"
                aspectRatio={820 / 312}
                cropperOptions={{ width: 820, height: 312 }}
                updateImage={(mediaFileMetadata: MediaFileMetadata | undefined) => setCoverImg(mediaFileMetadata)}
                getMediaData={(params) => {
                  setLoading({ profile: false, cover: true })
                  dispatchUploadCoverMediaFile(params)
                }}
                width="100%"
                height="340px"
              />
            )}
        </Box>
      </Stack>
      {!props.isWizard &&
        <Box sx={{ textAlign: 'right', width: '100%', mt: 2 }}>
          <PreloaderButton
            position='right'
            onClick={saveChanges}
            disableButton={(!profileImg && !coverImg) || loading.profile || loading.cover}
            variant="contained"
            text={`${t('facebookSettingsCommon:saveChanges')}`}
            loading={updatingProfileImage || updatingCoverImage}
          />
        </Box>}
    </>
  )
}
export default UserFacebookProfile
